import { isEmptyString } from '@racemap/utilities/functions/utils';
import type { SourceMapConsumer } from 'source-map';

interface ParsedStackLine {
  method: string;
  file: string;
  line?: number;
  column?: number;
}

const AT_MATCH_REGEX = /^\s*at\s+(.+?)\s+\((.+?):(\d+):(\d+)\)$/;
const SIMPLE_MATCH_REGEX = /^(.+?)@(.+?):(\d+):(\d+)$/;

export const parseStackLine = (line: string): ParsedStackLine | null => {
  // Parse different stack trace formats
  // Format: "    at methodName (file:line:column)" (with optional leading whitespace and spaces after 'at')
  //     or "methodName@file:line:column"
  const atMatch = line.match(AT_MATCH_REGEX);
  if (atMatch) {
    return {
      method: atMatch[1],
      file: atMatch[2],
      line: Number.parseInt(atMatch[3]),
      column: Number.parseInt(atMatch[4]),
    };
  }

  const simpleMatch = line.match(SIMPLE_MATCH_REGEX);
  if (simpleMatch) {
    return {
      method: simpleMatch[1],
      file: simpleMatch[2],
      line: Number.parseInt(simpleMatch[3]),
      column: Number.parseInt(simpleMatch[4]),
    };
  }

  // Fallback for lines without position
  const elements = line.split('@');
  return {
    method: elements[0] || '<anonymous>',
    file: elements[1] || '',
  };
};

export const mapStackTrace = async (stack: string): Promise<Array<string>> => {
  const lines = stack.split('\n');
  const mappedLines: Array<string> = [];

  // Check if source maps are available (they should be in production builds)
  const useSourceMaps = import.meta.env.PROD;

  for (const line of lines) {
    if (isEmptyString(line)) continue;

    const parsed = parseStackLine(line);
    if (!parsed) {
      mappedLines.push(line);
      continue;
    }

    if (useSourceMaps && parsed.line !== undefined && parsed.column !== undefined) {
      try {
        // Try to load and use source map
        const sourceMapConsumer = await getSourceMapConsumer(parsed.file);
        if (sourceMapConsumer) {
          const original = sourceMapConsumer.originalPositionFor({
            line: parsed.line,
            column: parsed.column,
          });

          if (original.source) {
            const originalFile = original.source.replace(/^webpack:\/\/\//, '');
            mappedLines.push(
              `${parsed.method}@${originalFile}:${original.line}:${original.column}`,
            );
            continue;
          }
        }
      } catch (error) {
        console.warn('Failed to map stack trace line:', error);
      }
    }

    // Fallback: clean up the file path
    const cleanFile = parsed.file.replace(`${window.origin}/`, '');
    const position = parsed.line !== undefined ? `:${parsed.line}:${parsed.column}` : '';
    mappedLines.push(`${parsed.method}@${cleanFile}${position}`);
  }

  return mappedLines;
};

// Cache for source map consumers
const sourceMapCache = new Map<string, SourceMapConsumer | null>();
const MAX_CACHE_SIZE = 10;

const getSourceMapConsumer = async (file: string): Promise<SourceMapConsumer | null> => {
  if (sourceMapCache.has(file)) {
    return sourceMapCache.get(file) || null;
  }

  try {
    // Dynamically import source-map library
    const { SourceMapConsumer } = await import('source-map');

    // Fetch the source map file
    const mapUrl = `${file}.map`;
    const response = await fetch(mapUrl);

    if (!response.ok) {
      return null;
    }

    const rawSourceMap = await response.json();
    const consumer = await new SourceMapConsumer(rawSourceMap);

    sourceMapCache.set(file, consumer);
    return consumer;
  } catch (error) {
    console.warn('Failed to load source map:', error);
    sourceMapCache.set(file, null);
    return null;
  } finally {
    // Maintain cache size
    if (sourceMapCache.size > MAX_CACHE_SIZE) {
      const oldestKey = sourceMapCache.keys().next().value;
      if (oldestKey) {
        const consumer = sourceMapCache.get(oldestKey);
        consumer?.destroy();
        sourceMapCache.delete(oldestKey);
      } else {
        sourceMapCache.clear();
      }
    }
  }
};

export const clearSourceMapCache = () => {
  for (const consumer of sourceMapCache.values()) {
    consumer?.destroy();
  }
  sourceMapCache.clear();
};
